import _ from "lodash";
import moment, { Moment } from "moment-timezone";
import Logger from "./logger";
import { Timestamp } from "firebase/firestore";

export interface TimeBreakdown {
  years?: number;
  days?: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export interface ToFirestoreTimestampOptions {
  startOfDay?: boolean;
}

/**
 * Validation result for birth year
 */
export interface BirthYearValidationResult {
  isValid: boolean;
  error: string | null;
}

export default class TimeUtils {
  static FullDateString = "YYYY-MM-DD";
  static FullDateTimeString = "YYYY-MM-DD HH:mm:ss";
  static PrettyTimeString = "ddd MMM do h:mm A";
  static RefreshRate = moment.duration(60, "seconds").asMilliseconds();

  static breakdown(ms: number, limitToHours = false): TimeBreakdown {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const years = Math.floor(days / 365);

    if (limitToHours) {
      return {
        hours: hours,
        minutes: minutes % 60,
        seconds: seconds % 60,
      };
    }

    return {
      years: years,
      days: days % 365,
      hours: hours % 24,
      minutes: minutes % 60,
      seconds: seconds % 60,
    };
  }

  static elapsedToString(
    breakdown: TimeBreakdown,
    limit = 2,
    includeSeconds = false
  ): string {
    const pieces: string[] = [];
    if (breakdown.years && breakdown.years > 0) {
      pieces.push(`${breakdown.years.toLocaleString()}y`);
    }
    if (breakdown.days && breakdown.days > 0) {
      pieces.push(`${breakdown.days.toLocaleString()}d`);
    }
    if (breakdown.hours > 0) {
      pieces.push(`${breakdown.hours.toLocaleString()}h`);
    }
    if (breakdown.minutes > 0) {
      pieces.push(`${breakdown.minutes.toLocaleString()}m`);
    }
    if (breakdown.seconds > 0 && includeSeconds) {
      pieces.push(`${breakdown.seconds.toLocaleString()}s`);
    }
    return _.join(_.take(pieces, limit), " ") || "0";
  }

  static formatTimer(ms: number): string {
    const breakdown = TimeUtils.breakdown(ms);
    if (breakdown.hours === 0) {
      return `${String(breakdown.minutes).padStart(2, "0")}:${String(
        breakdown.seconds
      ).padStart(2, "0")}`;
    }
    return `${String(breakdown.hours).padStart(2, "0")}:${String(
      breakdown.minutes
    ).padStart(2, "0")}:${String(breakdown.seconds).padStart(2, "0")}`;
  }

  static getAllMonthsSince(year: number | string): string[] {
    const currentDate = moment(); // Get the current date
    const currentYear = currentDate.year(); // Get the current year
    const currentMonth = currentDate.month(); // Get the current month (0-based)

    const months: string[] = [];

    // Loop through the years from the given year to the current year
    _.range(parseInt(String(year), 10), currentYear + 1).forEach((y) => {
      // Determine the start and end months for the current year
      const startMonth = 0;
      const endMonth = y === currentYear ? currentMonth : 11;

      // Loop through the months within the current year
      _.range(startMonth, endMonth + 1).forEach((m) => {
        const date = moment({ year: y, month: m, day: 1 }); // Create a moment date for the first day of the month
        months.push(date.format("YYYY-MM-DD"));
      });
    });

    return months;
  }

  static getTimezone(): string {
    return moment.tz.guess();
  }

  static inLast24Hours(date: Moment | Date | string): boolean {
    return moment().diff(date, "hours") < 24;
  }

  static inThisTimezone(
    date: Moment | Date | string,
    timezone: string | null = null
  ): Moment {
    if (!timezone) {
      return moment(date);
    }

    // 1. Create the moment in the source timezone
    const dateString = moment.isMoment(date)
      ? date.format("YYYY-MM-DD HH:mm:ss")
      : date;
    const originalMoment = moment.tz(dateString, timezone);

    // 2. Convert to the local timezone
    const convertedMoment = originalMoment.clone().tz(TimeUtils.getTimezone());
    return convertedMoment;
  }

  static parseDateStrictly(
    inputDate: string,
    inputTime: string | null = null
  ): Moment {
    const dateFormats = [
      ["YYYY", "MM", "DD"],
      ["YYYY", "M", "D"],
      ["MM", "DD", "YYYY"],
      ["M", "D", "YYYY"],
    ];
    const separators = ["-", "/"];
    const timeFormats = [
      "",
      "h:mm",
      "H:mm",
      "hh:mm",
      "HH:mm",
      "hh:mm",
      "HH:mm",
    ];
    const secondsFormat = ["", "s", "ss"];
    const timeSuffixes = ["", "A", "a", " A", " a"];

    const dateString = _.trim(`${inputDate} ${inputTime || ""}`);
    for (const date of dateFormats) {
      for (const separator of separators) {
        for (const time of timeFormats) {
          for (const seconds of secondsFormat) {
            for (const suffix of timeSuffixes) {
              const acceptableFormat = _.trim(
                `${date.join(separator)} ${time}${seconds}${suffix}`
              );
              const parsedDate = moment(dateString, acceptableFormat, true);
              if (parsedDate.isValid()) {
                return parsedDate;
              }
            }
          }
        }
      }
    }

    Logger.warn(`[ERROR] Could not parse: ${dateString}`);
    return moment.invalid();
  }

  static parseImportedDuration(input: string): number {
    const minutes = moment.duration(
      input,
      _.includes(input, ":") ? undefined : "minutes"
    );
    return minutes.as("milliseconds");
  }

  static recently(): Moment {
    return moment().subtract(30, "days");
  }

  static relativeDate(date: Moment | Date | string | null | undefined): string {
    if (!date) return "";

    const now = moment();
    const targetDate = moment(date);
    const diff = now.diff(targetDate);
    const duration = moment.duration(diff);

    // If it's in the future, just show the date
    if (diff < 0) {
      return targetDate.format("ddd MMM D @ h:mm A");
    }

    // Less than 1 minute
    if (duration.asMinutes() < 1) {
      return "Just now";
    }

    // Less than 1 hour
    if (duration.asHours() < 1) {
      const minutes = Math.floor(duration.asMinutes());
      return `${minutes}m ago`;
    }

    // Less than 24 hours
    if (duration.asDays() < 1) {
      const hours = Math.floor(duration.asHours());
      return `${hours}h ago`;
    }

    // Less than 7 days
    if (duration.asDays() < 7) {
      const days = Math.floor(duration.asDays());
      return `${days}d ago`;
    }

    return targetDate.format("MMM D");
  }

  static sameMonth(
    date1: Moment | null | undefined,
    date2: Moment | null | undefined
  ): boolean {
    if (!date1 || !date2) {
      return false;
    }
    return date1.month() === date2.month() && date1.year() === date2.year();
  }

  static span(
    startDate: Moment | null | undefined,
    endDate: Moment | null | undefined
  ): string[] {
    // If only one date is provided, return just that date
    if (!startDate && !endDate) {
      return [];
    }

    if (!startDate) {
      return [endDate!.format(TimeUtils.FullDateString)];
    }

    if (!endDate) {
      return [startDate.format(TimeUtils.FullDateString)];
    }

    // Ensure startDate is before or equal to endDate
    const start = startDate.isBefore(endDate) ? startDate : endDate;
    const end = startDate.isBefore(endDate) ? endDate : startDate;

    const dates: string[] = [];
    const current = start.clone().startOf("day");
    const endDay = end.clone().startOf("day");

    // Generate all dates between start and end, inclusive
    while (current.isSameOrBefore(endDay)) {
      dates.push(current.format(TimeUtils.FullDateString));
      current.add(1, "day");
    }

    return dates;
  }

  static spanMonth(
    startDate: Moment | null | undefined,
    endDate: Moment | null | undefined
  ): string[] {
    // Use the existing span function to get all dates
    const dates = TimeUtils.span(startDate, endDate);

    // Extract unique months from the dates and format as YYYY-MM-01
    const uniqueMonths = _.uniq(
      dates.map((date) => moment(date).startOf("month").format("YYYY-MM-01"))
    );

    return uniqueMonths;
  }

  static toMoment(
    date:
      | Moment
      | Date
      | string
      | null
      | undefined
      | Timestamp
      | { seconds: number; nanoseconds?: number }
  ): Moment | null {
    if (date === null || date === undefined || date === "") {
      return null;
    }
    if (moment.isMoment(date)) {
      if (date.isValid()) {
        return date;
      }
      return null;
    }
    // Check if it's a Timestamp instance or has toDate method
    if (date && typeof date === "object" && "toDate" in date) {
      const timestampLike = date as { toDate: () => Date };
      if (typeof timestampLike.toDate === "function") {
        try {
          const m = moment(timestampLike.toDate());
          return m.isValid() ? m : null;
        } catch {
          // If toDate fails, continue to other checks
        }
      }
    }

    // Handle plain objects with seconds and nanoseconds (Firestore Timestamp format)
    if (date && typeof date === "object" && "seconds" in date) {
      const dateObj = date as { seconds: number; nanoseconds?: number };
      if (typeof dateObj.seconds === "number") {
        // It's a plain object with seconds, convert to Date
        const jsDate = new Date(
          dateObj.seconds * 1000 + (dateObj.nanoseconds || 0) / 1000000
        );
        const m = moment(jsDate);
        return m.isValid() ? m : null;
      }
    }

    const m = moment(date);
    return m.isValid() ? m : null;
  }

  static totalMilliseconds(time: TimeBreakdown): number {
    const { days = 0, hours, minutes, seconds } = time;
    const totalMilliseconds =
      days * 24 * 60 * 60 * 1000 +
      hours * 60 * 60 * 1000 +
      minutes * 60 * 1000 +
      seconds * 1000;

    return totalMilliseconds;
  }

  static untilMidnight(): number {
    const now = moment();
    const midnight = moment().endOf("day");
    return midnight.diff(now, "milliseconds");
  }

  /**
   * Convert an input date to a Firestore Timestamp in UTC, preserving the instant in time.
   * Accepts moment instances, ISO strings, Date, or millis.
   * Options:
   *  - startOfDay: if true, snap to the start of the day before conversion
   */
  static toFirestoreTimestamp(
    date: Moment | Date | string | number | null | undefined,
    options: ToFirestoreTimestampOptions = {}
  ): Timestamp | null {
    if (date === null || date === undefined || date === "") {
      return null;
    }
    const { startOfDay = false } = options;
    const m = moment.isMoment(date) ? date.clone() : moment(date);
    if (!m.isValid()) {
      return null;
    }
    if (startOfDay) {
      m.startOf("day");
    }
    // Convert to UTC, preserving the exact instant in time
    const asUtc = m.utc();
    return Timestamp.fromDate(asUtc.toDate());
  }

  /**
   * Validate a birth year value
   * @param value - Birth year as string or number
   * @returns Validation result with isValid flag and error message (if any)
   */
  static validateBirthYear(
    value: string | number | null | undefined
  ): BirthYearValidationResult {
    // Empty value is valid (optional field)
    if (!value || (typeof value === "string" && value.trim() === "")) {
      return { isValid: true, error: null };
    }

    const valueStr = String(value);
    const yearInt = parseInt(valueStr, 10);
    const currentYear = moment().year();

    // Check if it's a valid number (digits only)
    if (!/^\d+$/.test(valueStr)) {
      return { isValid: false, error: "Please enter a valid year" };
    }

    // Check if it's exactly 4 digits
    if (valueStr.length !== 4) {
      return { isValid: false, error: "Please enter a 4-digit year" };
    }

    // Check bounds - can't be older than 100
    const oldestValidYear = currentYear - 100;
    if (yearInt < oldestValidYear) {
      return { isValid: false, error: "There's no way you're older than 100" };
    }

    if (yearInt > currentYear) {
      return { isValid: false, error: "Year cannot be in the future" };
    }

    return { isValid: true, error: null };
  }

  /**
   * Check if a birth year is valid (simple boolean check)
   * @param value - Birth year as string or number
   * @returns True if valid, false otherwise
   */
  static isValidBirthYear(value: string | number | null | undefined): boolean {
    return TimeUtils.validateBirthYear(value).isValid;
  }

  // ==================== Duration Input Validation ====================

  /**
   * Validate and sanitize time input value
   * @param value - Raw input value
   * @returns Sanitized numeric string (digits only)
   */
  static sanitizeInput(value: string): string {
    return value.replace(/\D/g, "");
  }

  /**
   * Validate hours value (0-23)
   * @param value - Hours value as string or number
   * @returns True if valid, false otherwise
   */
  static isValidHours(value: string | number): boolean {
    const num = typeof value === "string" ? parseInt(value, 10) : value;
    return !isNaN(num) && num >= 0 && num <= 23;
  }

  /**
   * Validate minutes or seconds value (0-59)
   * @param value - Minutes or seconds value as string or number
   * @returns True if valid, false otherwise
   */
  static isValidMinutesOrSeconds(value: string | number): boolean {
    const num = typeof value === "string" ? parseInt(value, 10) : value;
    return !isNaN(num) && num >= 0 && num <= 59;
  }

  /**
   * Validate time input for a specific field
   * @param value - Raw input value
   * @param field - Field type: "hours", "minutes", or "seconds"
   * @returns Object with isValid flag and sanitized value
   */
  static validateTimeInput(
    value: string,
    field: "hours" | "minutes" | "seconds"
  ): { isValid: boolean; sanitized: string } {
    const sanitized = TimeUtils.sanitizeInput(value);
    const num = parseInt(sanitized, 10);

    if (isNaN(num)) {
      return { isValid: false, sanitized };
    }

    if (field === "hours") {
      return {
        isValid: TimeUtils.isValidHours(num),
        sanitized: num > 23 ? "23" : sanitized,
      };
    }

    if (field === "minutes" || field === "seconds") {
      return {
        isValid: TimeUtils.isValidMinutesOrSeconds(num),
        sanitized: num > 59 ? "59" : sanitized,
      };
    }

    return { isValid: true, sanitized };
  }

  /**
   * Get the next field in the time input sequence
   * @param currentField - Current field: "hours", "minutes", or "seconds"
   * @returns Next field name or null if at the end
   */
  static getNextField(
    currentField: "hours" | "minutes" | "seconds"
  ): "minutes" | "seconds" | null {
    if (currentField === "hours") return "minutes";
    if (currentField === "minutes") return "seconds";
    return null;
  }

  /**
   * Create a reset time breakdown (all zeros)
   * @returns TimeBreakdown with all values set to 0
   */
  static resetBreakdown(): TimeBreakdown {
    return {
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }

  // ==================== Timer Display Utilities ====================

  /**
   * Calculate the percentage for hours progress (0-100)
   * @param hours - Number of hours
   * @returns Percentage value (0-100)
   */
  static hoursPercentage(hours: number): number {
    return (hours / 24) * 100;
  }

  /**
   * Calculate the percentage for minutes progress (0-100)
   * @param minutes - Number of minutes
   * @returns Percentage value (0-100)
   */
  static minutesPercentage(minutes: number): number {
    return (minutes / 60) * 100;
  }

  /**
   * Calculate the percentage for seconds progress (0-100)
   * @param duration - Duration in milliseconds
   * @returns Percentage value (0-100) based on seconds within a minute
   */
  static secondsPercentage(duration: number): number {
    return (((duration / 1000) % 60) / 60) * 100;
  }

  /**
   * Calculate all progress percentages for timer display
   * @param breakdown - Time breakdown object
   * @param duration - Duration in milliseconds
   * @returns Object with hours, minutes, and seconds percentages
   */
  static allPercentages(
    breakdown: TimeBreakdown,
    duration: number
  ): {
    hours: number;
    minutes: number;
    seconds: number;
  } {
    return {
      hours: TimeUtils.hoursPercentage(breakdown.hours || 0),
      minutes: TimeUtils.minutesPercentage(breakdown.minutes || 0),
      seconds: TimeUtils.secondsPercentage(duration),
    };
  }

  /**
   * Format days text (singular/plural)
   * @param days - Number of days
   * @returns Formatted string (e.g., "1 day" or "5 days")
   */
  static formatDays(days: number): string {
    return `${days} ${days === 1 ? "day" : "days"}`;
  }

  /**
   * Calculate font size based on elapsed time string length
   * @param elapsedTimeLength - Length of the elapsed time string
   * @returns Font size in rem
   */
  static fontSize(elapsedTimeLength: number): string {
    return elapsedTimeLength <= 5 ? "3.5rem" : "2.25rem";
  }

  // ==================== Date Formatting ====================

  /**
   * Format date/time for button display
   * @param date - Date to format (moment, Date, or dayjs object)
   * @returns Formatted string (e.g., "Mon Jan 15, 2024 02:30 PM")
   */
  static dateTimeButton(date: Moment | Date | { toDate: () => Date }): string {
    let dateObj: Date;
    if (moment.isMoment(date)) {
      dateObj = date.toDate();
    } else if (date && typeof date === "object" && "toDate" in date) {
      dateObj = (date as { toDate: () => Date }).toDate();
    } else {
      dateObj = date as Date;
    }
    return moment(dateObj).format("ddd MMM DD, YYYY hh:mm A");
  }

  /**
   * Format month and year for display
   * @param date - Date to format (moment, Date, or dayjs object)
   * @returns Formatted string (e.g., "January 2024")
   */
  static monthYear(date: Moment | Date | { toDate: () => Date }): string {
    let dateObj: Date;
    if (moment.isMoment(date)) {
      dateObj = date.toDate();
    } else if (date && typeof date === "object" && "toDate" in date) {
      dateObj = (date as { toDate: () => Date }).toDate();
    } else {
      dateObj = date as Date;
    }
    return moment(dateObj).format("MMMM YYYY");
  }
}
